import type { Timestamp } from "firebase/firestore";
import type * as Information from "./information";

export interface IUser {
  id: string;
  displayName: string;
  email: string;
  permissions: PermissionType[];
  company: ICompanyRef;
  photoUrl: string;
  contactNum: string;
  disabled: boolean;
  priority: number;
  accountType: EAccountType;
  address: string;
  state: string[];
  services?: string[];
  choosedService?: string[];
  providerData?: IProviderData;
  clientData?: IClientData;
  deviceId: string;
  browserId: string;
  surveyId: string;
  createdAt: Timestamp;
}

export interface ICompanyPricing {
  id: string;
  client: {
    servicePrice: Information.IStatePrice;
  };
}

export interface ICompanyRef {
  id: string;
  name: string;
  type: ECompanyType;
}

export interface IProviderData {
  regNum: string;
  vehicle: IVehicle[];
  leader: boolean;
}

export interface ICompany {
  id: string;
  name: string;
  regNum: string;
  contactNum: string;
  base: Information.ICoord[];
  state: string[];
  type: ECompanyType;
  services?: string[];
  // Client case email settings (client companies only). See docs/CLIENT_ASSIGNMENT_EMAIL.md.
  // Supervisor receivers: get every client case email for this company.
  notificationEmails?: string[];
  // RM added to casesPricing payment.client.pay for the email's Charges row (when it is shown).
  caseHandlingFee?: number;
  // true = only accident cases (fault code category "Accident") send client case emails; other
  // cases send none, apart from cancelling emails already sent. Absent = all cases.
  clientEmailAccidentOnly?: boolean;
  // CC on every client case email of this company (assignment, cancellation, completion), next to
  // the case workshop's own CC list. Never an email's only audience.
  clientEmailCcEmails?: string[];
  // true = the completion email of this company's cases shows the Charges row. Absent = hidden.
  // The assignment email follows the workshop's own `showCharges`.
  clientEmailShowCharges?: boolean;
}

export enum EVehicleType {
  MOTORCYCLE = "motorcycle",
  PRIVATE_VEHICLE = "private_vehicle",
  COMMERCIAL_VEHICLE = "commercial_vehicle",
  PRIVATE_AND_COMMERCIAL_VEHICLE = "private_and_commercial_vehicle",
  ELECTRIC_VEHICLE = "electric_vehicle",
}

export interface IVehicle {
  regNum: string;
  model: string;
  color: string;
  brand?: string;
  year?: string;
  type?: EVehicleType;
}

export interface IClientData {
  regNum: string;
  name: string;
  address: string;
  contactNum: string;
  leader: boolean;
}

export interface INotificationContent {
  id: string;
  createdAt: any;
  read: boolean;
  type: ENotificationType;
  title: string;
  body: string;
  custom: any;
}

export const enum ECompanyType {
  PROVIDER = "provider",
  CLIENT = "client",
  INTERNAL = "internal",
  OTHER = "other",
}

export const enum ENotificationType {
  CASE = "case",
  USER = "user",
  SYSTEM = "system",
  GENERAL = "general",
}

export const enum PermissionType {
  CASE_CREATE_SELF = "case_create_self",
  CASE_CLOSE_SELF = "case_close_self",
  CASE_CLOSE_OTHER = "case_close_other",
  CASE_START_SELF = "case_start_self",
  CASE_FINISH_SELF = "case_finish_self",
  CASE_START_OTHER = "case_start_other",
  CASE_FINISH_OTHER = "case_finish_other",
  CASE_ASSIGN_SELF = "case_assign_self",
  CASE_ASSIGN_OTHER = "case_assign_other",
  CASE_REASSIGN_SELF = "case_reassign_self",
  CASE_REASSIGN_OTHER = "case_reassign_other",
  CASE_UPDATE_OTHER = "case_update_other",
  CASE_READ_SELF = "case_read_self",
  CASE_READ_OTHER = "case_read_other",
  CASE_INTERNAL_REMARKS_READ = "case_internal_remarks_read",
  CASE_EXTERNAL_REMARKS_READ = "case_external_remarks_read",
  CASE_INTERNAL_REMARKS_CREATE = "case_internal_remarks_create",
  CASE_EXTERNAL_REMARKS_CREATE = "case_external_remarks_create",
  CASE_IMAGE_READ = "case_image_read",
  CASE_UPLOAD_PHOTO = "case_upload_photo",
  CASE_DELETE_PHOTO = "case_delete_photo",
  CASE_DELETE_REMARK = "case_delete_remark",
  CASE_CREATOR_READ = "case_creator_read",
  CASE_PROVIDER_READ = "case_provider_read",
  CASE_CLIENT_READ = "case_client_read",
  CASE_CUSTOMER_READ = "case_customer_read",
  CASE_PRICE_READ_OTHER = "case_price_read_other",
  CASE_PRICE_READ_PROVIDER = "case_price_read_provider",
  CASE_PRICE_READ_CLIENT = "case_price_read_client",
  CASE_PRICE_READ_CUSTOMER = "case_price_read_customer",
  CASE_LOG_READ = "case_log_read",
  CASE_ACCEPT_SELF = "case_accept_self",
  CASE_ACCEPT_OTHER = "case_accept_other",
  CASE_CREATE_ONLY_SELF_COMPANY = "case_create_only_self_company",
  CASE_CREATE_BACKDATE = "case_create_backdate",
  DOCUMENT_CLIENT_LIST_READ = "document_client_list_read",
  DOCUMENT_OWNER_LIST_READ = "document_owner_list_read",
  DOCUMENT_ADMIN_LIST_READ = "document_admin_list_read",
  DOCUMENT_PROVIDER_LIST_READ = "document_provider_list_read",
  DOCUMENT_USER_LIST_READ = "document_user_list_read",
  DOCUMENT_COMPANY_LIST_READ = "document_company_list_read",
  DOCUMENT_STATISTIC_READ = "document_statistic_read",
  USER_CREATE_OTHER = "user_create_other",
  USER_DELETE_SELF = "user_delete_self",
  USER_DELETE_OTHER = "user_delete_other",
  USER_UPDATE_SELF = "user_update_self",
  USER_UPDATE_OTHER = "user_update_other",
  USER_READ = "user_read",
  REQUEST_READ = "request_read",
  REQUEST_CREATE = "request_create",
  REQUEST_UPDATE = "request_update",
  REQUEST_DELETE = "request_delete",
  COMPANY_PRICE_READ = "company_price_read",
  COMPANY_READ = "company_read",
  COMPANY_UPDATE = "company_update",
  COMPANY_CREATE = "company_create",
  COMPANY_PRICE_UPDATE = "company_price_update",
  COMPANY_PRICE_COPY = "company_price_copy",
  TRACK_SELF = "track_self",
  TRACK_OTHER = "track_other",
  REPORT_READ = "report_read",
  DASHBOARD_READ = "dashboard_read",
  PROFILE_READ = "profile_read",
  PROFILE_UPDATE = "profile_update",
  NEWS_CREATE = "news_create",
  NEWS_UPDATE = "news_update",
  NEWS_DELETE = "news_delete",
  POLICY_READ = "policy_read",
  SETTINGS_READ = "settings_read",
  SETTINGS_UPDATE = "settings_update",
  ACCESS_WEB_APP = "access_web_app",
  DISABLE_ETA_REMINDER_SOUND = "disable_eta_reminder_sound",
  DISABLE_CASE_REQUEST_NOTIFICATION_SOUND = "disable_case_request_notification_sound",
  // Lets a fleet-owner (UserType.USER) read their OWN company's fleet vehicles via
  // GET /api/app/vehicles (the company is derived from the user, never a client param).
  VEHICLE_READ_SELF = "vehicle_read_self",
}

export const enum EAccountType {
  USER = "user",
  CLIENT = "client",
  PROVIDER = "provider",
  ADMIN = "admin",
  OWNER = "owner",
  // MAN truck/bus fleet owner (the MAN mobile app population)
  FLEET_USER = "fleet_user",
}

export enum UserType {
  // eg. Hong Seng Towing Company
  PROVIDER = "provider",
  // eg. AIA
  INSURER = "insurer",
  // eg. Ali, Muthu, Hertz
  USER = "user",
  // eg. Derris, Other admin
  INTERNAL = "internal",
  // MAN truck/bus fleet owner — customer-like; reads only their own company's fleet.
  FLEET_USER = "fleet_user",
}

export enum UserStatus {
  ACTIVE = "active",
  // removed
  SUSPENDED = "suspended",
  // user not logged in yet
  NEW = "new",
}

export interface ProviderList {
  company: {
    id: string;
    name: string;
  };
  id: string;
  leader: boolean;
  name: string;
  state: Array<string>;
}
